import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Copy, FileWarning, GitCompare, HardDrive, Inbox, LayoutDashboard, RefreshCw, Tags, Upload, Users } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { userName, useUsers } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { Chip, EmptyState, PageHeader, ReasonsList, RestrictedState, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { DOC6_STATUS, DOCREQ_STATUS, DUP6_STATUS, REPO6_STATUS, fmtDateTime } from "../lib/labels";

const TABS = [
  ["cuadro", "Cuadro de mando", LayoutDashboard], ["documentos", "Documentos", FileWarning],
  ["mios", "Mis documentos", Users], ["revision", "Pendientes de revisión", Inbox],
  ["solicitudes", "Solicitudes", Inbox], ["duplicados", "Duplicados", Copy],
  ["versiones", "Versiones y originales", GitCompare], ["repositorios", "Repositorios", HardDrive],
];

const DOC_ACTIONS = {
  clasificar: "Clasificar", validar: "Validar clasificación", crear_version: "Nueva versión",
  enviar_revision: "Enviar a revisión", solicitar_cambios: "Solicitar cambios", solicitar_aprobacion: "Solicitar aprobación",
  aprobar: "Aprobar", rechazar: "Rechazar", declarar_vigente: "Declarar vigente", sustituir: "Sustituir",
  archivar: "Archivar", aplicar_hold: "Aplicar hold", retirar_hold: "Retirar hold",
  solicitar_destruccion: "Solicitar destrucción", aprobar_destruccion: "Aprobar destrucción (demo)",
  bloquear: "Bloquear", resolver_bloqueo: "Resolver bloqueo",
};
// acciones contextualmente visibles por estado (el backend valida siempre)
const BY_STATUS = {
  recibido: ["clasificar"], pendiente_clasificacion: ["clasificar"], pendiente_validacion: ["validar"],
  en_extraccion: ["validar"], borrador: ["crear_version", "enviar_revision", "aplicar_hold"],
  en_revision: ["crear_version", "solicitar_cambios", "solicitar_aprobacion", "rechazar"],
  cambios_solicitados: ["crear_version", "enviar_revision"],
  pendiente_aprobacion: ["aprobar", "rechazar"], aprobado: ["declarar_vigente", "crear_version", "archivar", "aplicar_hold"],
  vigente: ["crear_version", "sustituir", "archivar", "aplicar_hold", "bloquear"],
  firmado: ["aplicar_hold"], sustituido: ["archivar", "solicitar_destruccion"], caducado: ["archivar", "solicitar_destruccion"],
  archivado: ["solicitar_destruccion", "aplicar_hold"], en_hold: ["retirar_hold"],
  pendiente_destruccion: ["aprobar_destruccion"], bloqueado: ["resolver_bloqueo"], rechazado: ["archivar"],
};
const REQ_ACTIONS = { borrador: ["solicitar"], solicitada: ["recibir", "cancelar"], vencida: ["recibir"], recibida: ["revisar"], en_revision: ["aceptar", "rechazar"], rechazada: ["reabrir"] };
const DUP_ACTIONS = { declarar_copia: "Declarar copia", declarar_version: "Declarar versión", mantener_ambos: "Mantener ambos", seleccionar_canonico: "Seleccionar canónico", archivar_copia: "Archivar copia", marcar_falso_positivo: "Falso positivo", escalar: "Escalar" };

export default function DocsCorp() {
  const { user } = useAuth();
  const ext = !!user?.is_contractor;
  const { map, users } = useUsers();
  const qc = useQueryClient();
  const [tab, setTab] = useState(ext ? "documentos" : "cuadro");
  const [dlg, setDlg] = useState(null); // {kind, doc/dup/req, action}
  const [f, setF] = useState({});
  const [err, setErr] = useState(null);
  const [flt, setFlt] = useState({});

  const refresh = () => qc.invalidateQueries();
  const { data: cuadro } = useQuery({ queryKey: ["docs-cuadro"], queryFn: async () => (await api.get("/documentos/cuadro")).data });
  const { data: docs } = useQuery({ queryKey: ["docs-list", flt], queryFn: async () => (await api.get("/documentos", { params: flt })).data });
  const { data: dups } = useQuery({ queryKey: ["docs-dups"], queryFn: async () => (await api.get("/documentos/duplicados")).data });
  const { data: reqs } = useQuery({ queryKey: ["docs-reqs"], queryFn: async () => (await api.get("/documentos/solicitudes")).data });
  const { data: repos } = useQuery({ queryKey: ["docs-repos"], queryFn: async () => (await api.get("/documentos/repositorios")).data });

  const mut = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body || {})).data,
    onSuccess: () => { toast.success("Operación aplicada"); setDlg(null); setF({}); setErr(null); refresh(); },
    onError: (e) => { setErr(e); toast.error(apiError(e) || "Error"); },
  });
  const open = (d) => { setDlg(d); setF({}); setErr(null); };

  const docCard = (d) => (
    <div key={d.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`doc-${d.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-semibold text-slate-800">{d.title}</span>
        <Chip map={DOC6_STATUS} value={d.status} testid={`doc-status-${d.id}`} />
        {d.hold && <ToneChip label="HOLD" tone="danger" testid={`doc-hold-${d.id}`} />}
        {d.confidentiality === "confidencial" && <ToneChip label="confidencial" tone="warning" />}
        {d.multi_location && <ToneChip label="multi-ubicación" tone="info" />}
        {d.naming_conforme === false && <ToneChip label="nombre no conforme" tone="warning" />}
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {d.document_code} · {d.document_type} · v{d.current_version}.0 · propietario {userName(map, d.owner_id) || "sin propietario"} · {d.original_location}
      </p>
      <p className="text-xs text-slate-400">
        canónico: {d.canonical_document === d.id ? "este documento" : d.canonical_document}
        {d.approved_version ? ` · aprobada v${d.approved_version}.0` : ""}{d.signed_version ? ` · firmada v${d.signed_version}.0` : ""}
        {d.expiration_date ? ` · caduca ${fmtDateTime(d.expiration_date).slice(0, 10)}` : ""}
      </p>
      <div className="mt-2 flex flex-wrap gap-1">
        {!ext && (BY_STATUS[d.status] || []).map((a) => (
          <Button key={a} size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`doc-${a}-${d.id}`}
            onClick={() => ["clasificar", "crear_version", "solicitar_cambios", "rechazar", "sustituir", "archivar", "aplicar_hold", "retirar_hold", "solicitar_destruccion", "resolver_bloqueo", "bloquear", "aprobar_destruccion"].includes(a)
              ? open({ kind: "doc", doc: d, action: a })
              : mut.mutate({ path: `/documentos/${d.id}/accion`, body: { action: a } })}>
            {DOC_ACTIONS[a]}
          </Button>
        ))}
        {!ext && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-blue-700" data-testid={`doc-naming-${d.id}`}
          onClick={() => open({ kind: "naming", doc: d })}>Nomenclatura</Button>}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="docs-corp-page">
      <PageHeader title="Documentos y correo — Gestión documental"
        subtitle="Catálogo documental único sobre múltiples repositorios: las ubicaciones externas se conservan; los duplicados se resuelven por decisión humana; las versiones aprobadas son inmutables."
        testid="docs-corp-title"
        actions={<>
          {ext && <ToneChip label="acceso externo: solo documentos compartidos (solo lectura)" tone="info" testid="docs-ext-note" />}
          {!ext && <Button onClick={() => open({ kind: "ingest" })} data-testid="doc-ingest-btn"><Upload className="mr-1 h-4 w-4" /> Ingerir documento</Button>}
        </>} />
      {ext && <RestrictedState module="el cuadro documental completo" detail="Vista externa limitada: solo ves los documentos compartidos expresamente contigo, en solo lectura." testid="docs-ext-banner" />}
      <div className="flex flex-wrap gap-1" data-testid="docs-tabs">
        {TABS.filter(([k]) => !ext || ["documentos", "mios"].includes(k)).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${tab === k ? "bg-blue-700 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>
        ))}
      </div>

      {tab === "cuadro" && cuadro && (
        <div className="space-y-4" data-testid="docs-cuadro">
          <section>
            <h2 className="ts-section-title mb-2">Requiere atención</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
              {Object.entries(cuadro.atencion).map(([k, v]) => (
                <button key={k} className="ts-card ts-card-hover p-3 text-left" data-testid={`kpi-${k}`}
                  onClick={() => { setFlt({}); setTab(k === "duplicados_pendientes" ? "duplicados" : k === "solicitudes_vencidas" ? "solicitudes" : "documentos"); }}>
                  <p className="ts-caption">{k.replaceAll("_", " ")}</p>
                  <p className={`font-display text-2xl font-bold ${v ? "text-slate-900" : "text-slate-500"}`}>{v}</p>
                </button>
              ))}
            </div>
          </section>
          <section>
            <h2 className="ts-section-title mb-2">Calidad</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              {Object.entries(cuadro.calidad).map(([k, v]) => (
                <div key={k} className="ts-card p-3" data-testid={`calidad-${k}`}>
                  <p className="ts-caption">{k.replaceAll("_", " ")}</p>
                  <p className={`font-display text-2xl font-bold ${v ? "text-slate-900" : "text-slate-500"}`}>{v}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {["documentos", "mios", "revision", "versiones"].includes(tab) && (
        <div className="space-y-2" data-testid={`docs-list-${tab}`}>
          <div className="flex gap-2">
            <Input placeholder="Buscar por título o código" className="max-w-xs" data-testid="docs-search"
              onKeyDown={(e) => e.key === "Enter" && setFlt({ ...flt, q: e.target.value })} />
            <Select value={flt.status || ""} onValueChange={(v) => setFlt({ ...flt, status: v || undefined })}>
              <SelectTrigger className="w-52" data-testid="docs-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>{Object.entries(DOC6_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {(docs || [])
            .filter((d) => tab === "mios" ? d.owner_id === map._me : tab === "revision" ? ["en_revision", "pendiente_aprobacion", "pendiente_validacion"].includes(d.status) : tab === "versiones" ? (d.current_version > 1 || d.canonical_document !== d.id || d.multi_location) : true)
            .map(docCard)}
          {docs && !docs.length && <EmptyState text="Sin documentos con estos filtros." testid="docs-empty" />}
        </div>
      )}

      {tab === "duplicados" && (
        <div className="space-y-2" data-testid="docs-dups">
          <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">La detección propone; una persona decide. Nunca se elimina automáticamente.</p>
          {(dups || []).map((d) => (
            <div key={d.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`dup-${d.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <Chip map={DUP6_STATUS} value={d.status} testid={`dup-status-${d.id}`} />
                <ToneChip label={`${Math.round((d.similarity || 0) * 100)}% · ${d.method}`} tone="info" />
              </div>
              <p className="mt-1 text-sm"><strong>A:</strong> {d.doc_a_info?.title} <span className="text-slate-400">({d.doc_a_info?.original_location})</span></p>
              <p className="text-sm"><strong>B:</strong> {d.doc_b_info?.title} <span className="text-slate-400">({d.doc_b_info?.original_location})</span></p>
              <p className="mt-1 text-xs text-slate-500">{d.reason}</p>
              {["detectado", "en_revision", "confirmado", "escalado"].includes(d.status) && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {Object.entries(DUP_ACTIONS).map(([a, l]) => (
                    <Button key={a} size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`dup-${a}-${d.id}`}
                      onClick={() => open({ kind: "dup", dup: d, action: a })}>{l}</Button>
                  ))}
                </div>
              )}
              {d.resolution && <p className="mt-1 text-xs text-emerald-700">Resuelto: {d.resolution} por {userName(map, d.resolved_by)} — {d.motivo}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === "solicitudes" && (
        <div className="space-y-2" data-testid="docs-reqs">
          {(reqs || []).map((r) => (
            <div key={r.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`req-${r.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold">{r.title}</span>
                <Chip map={DOCREQ_STATUS} value={r.status} testid={`req-status-${r.id}`} />
              </div>
              <p className="mt-1 text-xs text-slate-500">
                tipo {r.document_type} · responsable {userName(map, r.responsible_id)} · límite {fmtDateTime(r.due_date).slice(0, 10)} · criterio: {r.acceptance_criteria || "—"}
              </p>
              {r.rejection && <p className="text-xs text-red-600">Rechazada: {r.rejection.motivo} → corrección: {r.rejection.correccion}</p>}
              <div className="mt-2 flex flex-wrap gap-1">
                {(REQ_ACTIONS[r.status] || []).map((a) => (
                  <Button key={a} size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`req-${a}-${r.id}`}
                    onClick={() => open({ kind: "req", req: r, action: a })}>{a}</Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "repositorios" && (
        <div className="grid gap-2 md:grid-cols-2" data-testid="docs-repos">
          {(repos || []).map((r) => (
            <div key={r.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`repo-${r.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold">{r.name}</span>
                <Chip map={REPO6_STATUS} value={r.status} testid={`repo-status-${r.id}`} />
                <ToneChip label={r.provider} tone="muted" />
              </div>
              <p className="mt-1 text-xs text-slate-500">{r.documents_indexed} indexados · última sync {fmtDateTime(r.last_sync)} · política: {r.copy_policy}</p>
              {(r.errors || []).map((e, i) => <p key={i} className="text-xs text-red-600"><AlertTriangle className="mr-1 inline h-3 w-3" />{e.message}</p>)}
              <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" data-testid={`repo-sync-${r.id}`}
                onClick={() => mut.mutate({ path: `/documentos/repositorios/${r.id}/sincronizar` })}>
                <RefreshCw className="mr-1 h-3 w-3" /> Sincronizar
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={() => setDlg(null)}>
        <DialogContent data-testid="docs-dialog">
          <DialogHeader><DialogTitle>
            {dlg?.kind === "ingest" && "Ingerir documento (demo)"}
            {dlg?.kind === "doc" && `${DOC_ACTIONS[dlg.action]} — ${dlg.doc.title}`}
            {dlg?.kind === "dup" && `${DUP_ACTIONS[dlg.action]} — ${dlg.dup.id}`}
            {dlg?.kind === "req" && `${dlg.action} — ${dlg.req.title}`}
            {dlg?.kind === "naming" && `Nomenclatura — ${dlg.doc.title}`}
          </DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              {dlg.kind === "ingest" && (
                <>
                  <div><Label>Título *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="f-title" /></div>
                  <div><Label>Contenido (texto demo, se calcula hash) *</Label><Textarea value={f.content || ""} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="f-content" /></div>
                  <div><Label>Repositorio *</Label>
                    <Select value={f.repository_id || ""} onValueChange={(v) => setF({ ...f, repository_id: v })}>
                      <SelectTrigger data-testid="f-repo"><SelectValue placeholder="Repositorio" /></SelectTrigger>
                      <SelectContent>{(repos || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {dlg.kind === "doc" && dlg.action === "clasificar" && (
                <div><Label>Tipo documental *</Label>
                  <Select value={f.document_type || ""} onValueChange={(v) => setF({ ...f, document_type: v })}>
                    <SelectTrigger data-testid="f-dtype"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>{["contrato", "factura", "acta", "poliza", "permiso", "certificacion", "informe_tecnico", "reporting", "planning", "otro"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {dlg.kind === "doc" && dlg.action === "crear_version" && (
                <>
                  <div><Label>Motivo de la nueva versión *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
                  <div><Label>Contenido (opcional)</Label><Textarea value={f.content || ""} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="f-content" /></div>
                </>
              )}
              {dlg.kind === "doc" && ["solicitar_cambios", "rechazar", "archivar", "aplicar_hold", "retirar_hold", "solicitar_destruccion", "resolver_bloqueo", "bloquear"].includes(dlg.action) && (
                <div><Label>Motivo *</Label><Textarea value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
              )}
              {dlg.kind === "doc" && dlg.action === "sustituir" && (
                <>
                  <div><Label>ID del documento que sustituye *</Label><Input value={f.nueva_version_id || ""} onChange={(e) => setF({ ...f, nueva_version_id: e.target.value })} data-testid="f-sustituto" /></div>
                  <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
                </>
              )}
              {dlg.kind === "doc" && dlg.action === "aprobar_destruccion" && (
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.checked })} data-testid="f-confirm" /> Confirmo la destrucción SIMULADA (demo; se conserva la auditoría)</label>
              )}
              {dlg.kind === "dup" && (
                <>
                  <p className="text-xs text-slate-500">A: {dlg.dup.doc_a_info?.title} · B: {dlg.dup.doc_b_info?.title}. Ninguna resolución elimina datos sin guardas.</p>
                  {["declarar_copia", "seleccionar_canonico", "declarar_version"].includes(dlg.action) && (
                    <div><Label>Documento canónico/base</Label>
                      <Select value={f.canonico || "a"} onValueChange={(v) => setF({ ...f, canonico: v, base: v })}>
                        <SelectTrigger data-testid="f-canonico"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="a">A (original)</SelectItem><SelectItem value="b">B</SelectItem></SelectContent>
                      </Select>
                    </div>
                  )}
                  {dlg.action === "archivar_copia" && (
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.relaciones_migradas} onChange={(e) => setF({ ...f, relaciones_migradas: e.target.checked })} data-testid="f-relmig" /> He revisado/migrado las relaciones de la copia</label>
                  )}
                  <div><Label>Motivo</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
                </>
              )}
              {dlg.kind === "req" && (
                <>
                  {dlg.action === "recibir" && <div><Label>ID del documento recibido *</Label><Input value={f.document_id || ""} onChange={(e) => setF({ ...f, document_id: e.target.value })} data-testid="f-docid" /></div>}
                  {dlg.action === "rechazar" && (
                    <>
                      <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
                      <div><Label>Corrección requerida *</Label><Input value={f.correccion || ""} onChange={(e) => setF({ ...f, correccion: e.target.value })} data-testid="f-correccion" /></div>
                    </>
                  )}
                  {dlg.action === "cancelar" && <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>}
                </>
              )}
              {dlg.kind === "naming" && (
                <NamingView docId={dlg.doc.id} />
              )}
              {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="docs-error">{apiError(err)}</p>)}
              {dlg.kind !== "naming" && (
                <Button data-testid="docs-dialog-submit" disabled={mut.isPending}
                  onClick={() => {
                    if (dlg.kind === "ingest") mut.mutate({ path: "/documentos/ingerir", body: f });
                    else if (dlg.kind === "doc") mut.mutate({ path: `/documentos/${dlg.doc.id}/accion`, body: { action: dlg.action, ...f } });
                    else if (dlg.kind === "dup") mut.mutate({ path: `/documentos/duplicados/${dlg.dup.id}/resolver`, body: { action: dlg.action, ...f } });
                    else if (dlg.kind === "req") mut.mutate({ path: `/documentos/solicitudes/${dlg.req.id}/accion`, body: { action: dlg.action, ...f } });
                  }}>Confirmar</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NamingView({ docId }) {
  const { data } = useQuery({ queryKey: ["naming", docId], queryFn: async () => (await api.get(`/documentos/${docId}/nomenclatura`)).data });
  if (!data) return null;
  return (
    <div className="space-y-1 text-xs" data-testid="naming-view">
      <p><strong>Actual:</strong> {data.current}</p>
      <p><strong>Recomendado:</strong> {data.recommended}</p>
      <p><strong>Regla:</strong> <code>{data.rule}</code></p>
      <p><strong>Conflictos:</strong> {data.conflicts.length ? data.conflicts.join(", ") : "ninguno"}</p>
      <p className="text-slate-500">{data.impact}</p>
      {data.conforme ? <ToneChip label="nombre conforme" tone="success" testid="naming-ok" /> : <ToneChip label="no conforme" tone="warning" testid="naming-ko" />}
    </div>
  );
}
